import { useEffect, useMemo } from "react";

// custom markup
import TableNew, { CELL_TYPES } from "../../../shared/Table";
import RefreshButton from "../../../shared/ui/Refresh.Button";

import CreditApplicationsTableButtons from "./CreditApplicationTableCells/CreditApplicationsTableButtons";
import { applicationConnector } from "../../../services/applications/connector";
import { compose } from "redux";

// formatters and other
import _ from "underscore";

// icons
import ClaimsWrapper from "../../../shared/ui/Claim.Wrapper";
import { usersConnector } from "../../../services/users/connectors/usersConnector";
import { allClaimsOtherThanAml } from "../../../shared/ClaimLists/Claim.Aml.All.List";
import TableCellCreditApplicationActions from "../../../services/applications/tables/TableCellCreditApplicationActions";
import TableCellReviewCreditApplicationActions from "../../../services/applications/tables/TableCellReviewCreditApplicationActions";
import loanConnector from "../../../services/loan/connector";
import PersonalCreditApplicationDetails from "./CreditApplicationDetails/Personal.Credit.Application.Details.Component";
import PropTypes from "prop-types";
import { getDeclineReasonText } from "./CreditApplicationDetails/CreditApplicationDetailsCmps/helper";

const PersonalCreditApplicationsComponent = ({
  getCreditApplicationsForAccount,
  getRowAdditionalDataPersonal,
  changeApplicationListPage,
  getAccountInfoForId,
  approveRejectCreditOffersFromBorrowerPage,
  creditApplications,
  accountInfos,
  reviewingCreditApplications,
  accountId,
  approveCreditOffer,
  rejectCreditApplication,
  rejectCreditOffer,
  putOnHold,
  removeFromOnHold,
  loans,
  currentUser,
  expire,
}) => {
  const sortedData = useMemo(() => {
    return _.sortBy(
      creditApplications.data.concat(reviewingCreditApplications.data),
      (application) => {
        return new Date(application.UserInfoFinalizedAt).valueOf();
      },
    ).reverse();
  }, [creditApplications.data, reviewingCreditApplications.data]);

  useEffect(() => {
    getCreditApplicationsForAccount(accountId, null);
    changeApplicationListPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.Ssn]);

  let refreshFunction = getCreditApplicationsForAccount.bind(
    this,
    accountId,
    null,
  );
  let approveRejectFunction = approveRejectCreditOffersFromBorrowerPage.bind(
    this,
    accountId,
  );

  let props = {
    getCreditApplicationsForAccount,
    getRowAdditionalDataPersonal,
    changeApplicationListPage,
    getAccountInfoForId,
    approveRejectCreditOffersFromBorrowerPage,
    creditApplications,
    accountInfos,
    reviewingCreditApplications,
    accountId,
    approveCreditOffer,
    rejectCreditApplication,
    rejectCreditOffer,
    putOnHold,
    removeFromOnHold,
    loans,
  };

  return (
    <div className="applications-layout body-height content-padding">
      <RefreshButton onRefreshClick={refreshFunction} />

      <h3>Loanstep: Credit Applications</h3>
      {!creditApplications.fetched ? (
        <div> Loading... </div>
      ) : (
        <TableNew
          {...props}
          columns={getColumns()}
          tableKey={"PersonalCreditApplications"}
          selectId={"Id"}
          items={sortedData}
          showDetailsFun={addMoreInfo}
          actionColumnWidth={360}
          detailsRenderer={(item) => {
            return getDetails(item, accountInfos, approveRejectFunction);
          }}
          aboveContentRenderer={(getSelected, rows) => {
            return (
              <ClaimsWrapper claimAuth={allClaimsOtherThanAml}>
                <CreditApplicationsTableButtons
                  getSelected={getSelected}
                  rows={rows}
                  {...props}
                />
              </ClaimsWrapper>
            );
          }}
        />
      )}
      <br />
    </div>
  );

  function getColumns() {
    return [
      {
        key: "BorrowerId",
        label: "Get ssn",
        visible: true,
        filter: false,
        type: CELL_TYPES.ssn,
      },
      {
        key: "UserInfoFinalizedAt",
        label: "UserInfo Finalized",
        visible: true,
        type: CELL_TYPES.date,
      },
      {
        key: "SubmittedAt",
        label: "Submitted",
        visible: true,
        type: CELL_TYPES.date,
      },
      {
        key: "EvaluatedAt",
        label: "Evaluated",
        visible: true,
        type: CELL_TYPES.date,
      },
      {
        key: "DeclineReason",
        label: "Decline Reason",
        visible: true,
        type: CELL_TYPES.text,
        renderer: (value) => {
          return <span>{getDeclineReasonText(value)}</span>;
        },
      },
      {
        key: "LoanId",
        label: "Associated Loan",
        visible: true,
        type: CELL_TYPES.text,
      },
      {
        key: "Id",
        label: "Actions",
        visible: true,
        filter: false,
        renderer: (value, item) => {
          return item.OnHold ? (
            <TableCellReviewCreditApplicationActions
              removeFromOnHold={removeFromOnHold}
              value={value}
              item={item}
              getAccountInfoForId={getAccountInfoForId}
              accountInfos={accountInfos}
            />
          ) : (
            <ClaimsWrapper claimAuth={allClaimsOtherThanAml}>
              <TableCellCreditApplicationActions
                putOnHold={putOnHold}
                expire={expire}
                item={item}
              />
            </ClaimsWrapper>
          );
        },
      },
    ];
  }

  function addMoreInfo(row) {
    getRowAdditionalDataPersonal(
      row.BorrowerId,
      row.Identity,
      row.BankAccountId,
      row.Id,
      row.LoanId,
    );
  }

  function getDetails(application, accountInfos, approveRejectFunction) {
    let loan = loans.data.find((l) => l.Id === application.LoanId);
    return (
      <PersonalCreditApplicationDetails
        application={application}
        accountInfos={accountInfos}
        approveCreditOffer={approveCreditOffer}
        rejectCreditOffer={rejectCreditOffer}
        approveRejectCreditOffers={approveRejectFunction}
        rejectCreditApplication={rejectCreditApplication}
        loan={loan}
      />
    );
  }
};

PersonalCreditApplicationsComponent.propTypes = {
  creditApplications: PropTypes.object,
  currentUser: PropTypes.object,
  accountInfos: PropTypes.object,
  reviewingCreditApplications: PropTypes.object,
  accountId: PropTypes.string,
  loans: PropTypes.object,
  approveCreditOffer: PropTypes.func,
  rejectCreditApplication: PropTypes.func,
  rejectCreditOffer: PropTypes.func,
  putOnHold: PropTypes.func,
  removeFromOnHold: PropTypes.func,
  getCreditApplicationsForAccount: PropTypes.func,
  getRowAdditionalDataPersonal: PropTypes.func,
  changeApplicationListPage: PropTypes.func,
  getAccountInfoForId: PropTypes.func,
  approveRejectCreditOffersFromBorrowerPage: PropTypes.func,
  expire: PropTypes.func,
};

export const CreditApplicationForUser = compose(
  applicationConnector,
  usersConnector,
  loanConnector,
)(PersonalCreditApplicationsComponent);
